import { useEffect, useMemo, useState } from 'react';
import type { JobTaskView } from '@codebox/shared';
import { TaskCarousel } from './TaskCarousel';

/**
 * Full-screen build overlay.
 *
 * Two things it has to get right, both learned the hard way:
 *
 * 1. ONE layout, always. It used to render a hardcoded phase list before the real checklist arrived
 *    and the carousel after, so the screen visibly re-laid-out mid-build and read as "it started
 *    over". Both states now feed the same carousel; only the rows differ.
 * 2. Say WHERE you are. Every stage looked identical, so a learner watching for two minutes had no
 *    way to tell progress from a reset. The stepper across the top is the coarse "you are here".
 */

/**
 * The coarse stages. `phases` is the FALLBACK only — the real source is `task.stage`, which the
 * server sets, because inferring the stage from the phase let the stepper claim "Building" while the
 * running task was literally "Plan the build".
 */
const STAGES = [
  { key: 'plan', label: 'Planning', phases: ['plan'] },
  { key: 'build', label: 'Building', phases: ['html', 'images', 'css', 'js', 'delta'] },
  { key: 'review', label: 'Reviewing', phases: [] as string[] },
  { key: 'finish', label: 'Finishing', phases: ['persist', 'done'] },
] as const;

/** Shown only until the real checklist arrives — the same shape so the carousel can render either. */
const PHASE_ROWS: Array<{ key: string; label: string; stage: JobTaskView['stage'] }> = [
  { key: 'plan', label: 'Planning your build', stage: 'plan' },
  { key: 'html', label: 'Writing the structure', stage: 'build' },
  { key: 'images', label: 'Generating pictures', stage: 'build' },
  { key: 'css', label: 'Making it look right', stage: 'build' },
  { key: 'js', label: 'Wiring up behavior', stage: 'build' },
  { key: 'persist', label: 'Saving everything', stage: 'finish' },
];

const TIPS = [
  'Be specific — "make the button green and bigger" works better than "make it pop".',
  'One change per prompt gets you further than five changes at once.',
  'Planning is free. Talk an idea through before you spend a spark on it.',
  'Editing text and swapping pictures never costs you anything.',
  'Bigger ideas take a couple of minutes to build. Hang tight — it will be worth it.',
  'Publish when it feels right, then share the link and watch someone else play it.',
  'Every prompt is saved as a version, so you can always go back.',
  'If something breaks, Sparky fixes it for free — twice, then teaches you the fix.',
];

interface GenerationOverlayProps {
  phase: string | null;
  narration: string | null;
  error: string | null;
  /** the real per-job checklist; when present it replaces the phase rows below */
  tasks?: JobTaskView[];
  /** live updates are not arriving — say so rather than showing a frozen checklist */
  offline?: boolean;
  onCancel: () => void;
  onRetry: () => void;
}

export function GenerationOverlay({ phase, narration, error, tasks = [], offline = false, onCancel, onRetry }: GenerationOverlayProps) {
  const [elapsed, setElapsed] = useState(0);
  const [tip, setTip] = useState(() => Math.floor(Math.random() * TIPS.length));

  useEffect(() => {
    const timer = window.setInterval(() => setElapsed(e => e + 1), 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => setTip(t => (t + 1) % TIPS.length), 5500);
    return () => window.clearInterval(timer);
  }, []);

  const clock = `${Math.floor(elapsed / 60)}:${String(elapsed % 60).padStart(2, '0')}`;

  /*
   * The stage comes from the TASK, never from a guess. Running task first; if nothing is running,
   * the first unfinished row; only with no tasks at all do we fall back to the phase mapping.
   */
  const done = (t: JobTaskView) => t.status === 'done' || t.status === 'failed' || t.status === 'skipped';
  const current = tasks.find(t => t.status === 'running') ?? tasks.find(t => !done(t)) ?? null;

  const stageIndex = (() => {
    if (current) {
      const i = STAGES.findIndex(s => s.key === current.stage);
      if (i >= 0) return i;
    }
    if (tasks.length > 0) return STAGES.length - 1; // every row finished: we are wrapping up
    const byPhase = STAGES.findIndex(s => (phase ? (s.phases as readonly string[]).includes(phase) : false));
    return Math.max(byPhase, 0);
  })();

  /*
   * Progress WITHIN the current stage. Real arithmetic wherever the stage owns tasks; a time-based
   * ease only when it does not, so the bar always moves without ever claiming to be finished.
   */
  const stageProgress = (() => {
    const mine = tasks.filter(t => t.stage === STAGES[stageIndex].key);
    if (mine.length > 0) return mine.filter(done).length / mine.length;
    return Math.min(0.9, 1 - Math.exp(-elapsed / 25));
  })();

  /*
   * Replanning is SHOWN, not disguised. When the agent pipeline gives up and the fixed pipeline
   * reseeds, the checklist restarts with another planning row — the stepper legitimately steps back,
   * and saying so beats a stepper that claims Building over a list of planning tasks.
   */
  const replanning = stageIndex === 0 && tasks.length > 0 && tasks.some(done);

  /*
   * Before the checklist exists, show the phase rows through the SAME carousel. Without this the
   * planning screen and the building screen were two different layouts and the swap looked like a
   * restart. Statuses are derived from the current phase so the rows behave like real tasks.
   */
  const rows: JobTaskView[] = useMemo(() => {
    if (tasks.length > 0) return tasks;
    const activePhase = PHASE_ROWS.findIndex(p => p.key === phase);
    return PHASE_ROWS.map((p, i) => ({
      id: p.key,
      order: i,
      title: p.label,
      detail: null,
      stage: p.stage,
      status:
        activePhase === -1
          ? i === 0
            ? 'running' // no phase yet: the first row is what is happening, the rest are pending
            : 'planned'
          : i < activePhase
            ? 'done'
            : i === activePhase
              ? 'running'
              : 'planned',
    }));
  }, [tasks, phase]);

  return (
    /* Full-bleed, and a column: header and stepper at the top, the carousel centered in the space
       that is left, tips flush to the bottom. The old centered card left a big empty gap under the
       list and floated the tips in the middle of nowhere. */
    <div className="fixed inset-0 z-[110] flex flex-col bg-surface">
      {/* the clock is the only chrome: flush to the corner, big enough to read at a glance,
          with a sweeping second hand so its meaning is obvious without a label */}
      <span className="absolute right-6 top-5 flex items-center gap-2 text-lg font-bold tabular-nums text-muted sm:right-8 sm:top-6">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <circle cx="12" cy="12" r="9" />
          <g style={{ transformOrigin: '12px 12px' }} className="animate-[spin_6s_linear_infinite]">
            <path d="M12 12V6.5" strokeLinecap="round" />
          </g>
          <path d="M12 12l3.5 2" strokeLinecap="round" strokeOpacity="0.5" />
        </svg>
        {clock}
      </span>

      <div className="modal-in mx-auto flex h-full w-full max-w-[680px] flex-col px-6 pb-8 pt-16 sm:px-10">
        {/*
          The stepper is the header now — no logo, no title. It is centered, and the connector out of
          the current stage carries a live fill (real task progress while building, a time-based ease
          otherwise) with a flowing sheen, so even two visually-identical minutes read as movement
          rather than a reset.
        */}
        <ol className="mx-auto flex w-full max-w-[540px] items-center" aria-label="Build stages">
          {STAGES.map((stage, i) => {
            const done = i < stageIndex;
            const current = i === stageIndex && !error;
            const fill = done ? 1 : current ? stageProgress : 0;
            return (
              <li key={stage.key} className={['flex items-center', i < STAGES.length - 1 ? 'flex-1' : ''].join(' ')}>
                <span className="flex flex-col items-center gap-1.5">
                  <span
                    className={[
                      'relative grid h-8 w-8 place-items-center rounded-full text-xs font-bold transition-all duration-500',
                      done
                        ? 'bg-accent text-accent-ink'
                        : current
                          ? 'bg-accent/15 text-accent-deep'
                          : 'bg-page text-muted',
                    ].join(' ')}
                  >
                    {current && <span className="absolute inset-0 animate-ping rounded-full bg-accent/25" />}
                    <span className="relative">{done ? '✓' : i + 1}</span>
                  </span>
                  <span
                    className={[
                      'text-xs font-semibold transition-colors duration-500',
                      current ? 'text-ink' : 'text-muted',
                    ].join(' ')}
                  >
                    {stage.label}
                  </span>
                </span>
                {i < STAGES.length - 1 && (
                  <span className="mx-3 mb-5 h-1 flex-1 overflow-hidden rounded-full bg-line/60">
                    <span
                      className={['block h-full rounded-full bg-accent transition-all duration-700 ease-out', current ? 'flow-sheen' : ''].join(' ')}
                      style={{ width: `${Math.round(fill * 100)}%` }}
                    />
                  </span>
                )}
              </li>
            );
          })}
        </ol>

        {/* An honest step BACK reads better than a stepper that lies about where it is. */}
        {replanning && !error && (
          <p className="anim-in mt-4 text-center text-xs text-muted">Taking a different route — replanning.</p>
        )}

        {offline && !error && (
          <p className="mt-5 rounded-xl bg-page px-3 py-2 text-center text-xs text-muted">
            Lost the live connection — your app is still building. Reconnecting…
          </p>
        )}

        {/* the carousel owns the middle; it grows to fill whatever is left between stepper and tips */}
        <div className="flex min-h-0 flex-1 items-center justify-center py-4">
          {error ? (
            <div className="w-full text-center">
              <h2 className="text-lg font-bold text-ink">That build hit a snag</h2>
              <div className="mt-3 rounded-xl bg-danger/10 px-4 py-3 text-sm text-danger">{error}</div>
            </div>
          ) : (
            <div className="w-full rounded-2xl bg-page/70 px-6 py-8 sm:px-10"><TaskCarousel tasks={rows} narration={narration} /></div>
          )}
        </div>

        {/* tips — a single quiet line pinned to the bottom, dots as the only ornament */}
        {!error && (
          <div className="pb-1 text-center">
            {/* keyed so each tip re-enters with the rise animation instead of snapping in place */}
            <p key={tip} className="anim-in mx-auto flex min-h-[63px] max-w-[52ch] items-center justify-center text-sm text-muted">
              {TIPS[tip]}
            </p>
            <div className="mx-auto mt-3 flex w-24 justify-center gap-1.5">
              {TIPS.map((_, i) => (
                <span
                  key={i}
                  className={[
                    'h-1 rounded-full transition-all duration-500',
                    i === tip ? 'w-4 bg-accent' : 'w-1 bg-line',
                  ].join(' ')}
                />
              ))}
            </div>
          </div>
        )}

        <div className="mt-4 flex justify-center gap-2">
          {error ? (
            <>
              <button
                type="button"
                onClick={onCancel}
                className="inline-flex h-11 items-center justify-center rounded-xl px-5 text-sm font-semibold text-ink transition hover:bg-page"
              >
                Close
              </button>
              <button
                type="button"
                onClick={onRetry}
                className="inline-flex h-11 items-center justify-center rounded-xl bg-accent px-5 text-sm font-semibold text-accent-ink transition hover:brightness-95"
              >
                Try again
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={onCancel}
              className="inline-flex h-11 items-center justify-center rounded-xl px-5 text-sm font-semibold text-muted transition hover:bg-page hover:text-ink"
            >
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
